<template>
  <v-list-item
    :class="['notification-item', { 'notification-unread': !notification.read }]"
    @click="handleClick"
    :ripple="true"
  >
    <template v-slot:prepend>
      <v-avatar
        :color="notificationColor"
        size="40"
        class="notification-icon"
      >
        <v-icon :icon="notificationIcon" color="white" size="20" />
      </v-avatar>
    </template>

    <v-list-item-title class="notification-title">
      {{ notification.title }}
    </v-list-item-title>

    <v-list-item-subtitle class="notification-message">
      {{ notification.message }}
    </v-list-item-subtitle>

    <template v-slot:append>
      <div class="notification-meta">
        <span class="notification-time">{{ timeAgo }}</span>
        <div v-if="showActions" class="notification-actions">
          <v-btn
            v-if="!notification.read"
            icon="mdi-check"
            size="x-small"
            variant="text"
            @click.stop="markAsRead"
            title="Mark as read"
          />
          <v-btn
            icon="mdi-delete"
            size="x-small"
            variant="text"
            @click.stop="deleteNotification"
            title="Delete"
          />
        </div>
      </div>
      <v-icon
        v-if="!notification.read"
        icon="mdi-circle"
        size="8"
        color="primary"
        class="unread-indicator"
      />
    </template>
  </v-list-item>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import type { Notification } from '@/types'
import {
  getNotificationIcon,
  getNotificationColor,
  formatTimeAgo,
  getNotificationRoute,
} from '@/utils/notifications'

interface Props {
  notification: Notification
  showActions?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showActions: false,
})

const emit = defineEmits<{
  (e: 'click', notification: Notification): void
  (e: 'markAsRead', notificationId: string): void
  (e: 'delete', notificationId: string): void
}>()

const router = useRouter()

// Computed properties
const notificationIcon = computed(() => getNotificationIcon(props.notification.type))
const notificationColor = computed(() => getNotificationColor(props.notification.type))
const timeAgo = computed(() => formatTimeAgo(props.notification.created_at))

// Methods
const handleClick = () => {
  emit('click', props.notification)

  // Mark as read if unread
  if (!props.notification.read) {
    emit('markAsRead', props.notification.id)
  }

  // Navigate to the related content
  const route = getNotificationRoute(props.notification)
  router.push(route)
}

const markAsRead = () => {
  emit('markAsRead', props.notification.id)
}

const deleteNotification = () => {
  emit('delete', props.notification.id)
}
</script>

<style scoped lang="scss">
.notification-item {
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  &:hover {
    background: rgba(102, 126, 234, 0.05);
  }

  &.notification-unread {
    background: rgba(102, 126, 234, 0.03);

    &:hover {
      background: rgba(102, 126, 234, 0.08);
    }
  }
}

.notification-icon {
  flex-shrink: 0;
}

.notification-title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.notification-message {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.4;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.notification-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.notification-time {
  font-size: 0.75rem;
  color: #999;
  white-space: nowrap;
}

.notification-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;

  .notification-item:hover & {
    opacity: 1;
  }
}

.unread-indicator {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
}

@media (max-width: 600px) {
  .notification-title {
    font-size: 0.85rem;
  }

  .notification-message {
    font-size: 0.8rem;
  }

  .notification-time {
    font-size: 0.7rem;
  }
}
</style>
